import React, { FC, useContext, useMemo, useState } from 'react';

import {
  ComputerIcon,
  getIcon,
  TIconColor,
  WorkerIcon,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';

import { LocalizationContext } from 'localization';
import { ClientModal, DepositModal } from 'modals';
import { getLogConfigMap } from 'utils';

import { getNormalizeLogs } from '../utils';
import { getGuest } from '../utils/getGuest';
import { Description } from './Description';
import { ILogMobileProps } from './interfaces';
import s from './LogMobile.module.scss';

export const LogMobile: FC<ILogMobileProps> = ({ eventLogs }) => {
  const { t } = useContext(LocalizationContext);

  const events = useMemo(() => {
    if (!eventLogs) return [];
    return getNormalizeLogs(eventLogs);
  }, [eventLogs]);

  const LOG_CONFIGS_MAP = useMemo(() => getLogConfigMap(t), [t]);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [selectedClientUUID, setSelectedClientUUID] = useState<string | null>(
    null
  );

  const [isDepositOpen, setIsDepositOpen] = useState(false);

  const getIconColor = (color: string): TIconColor => {
    const colorIconMap = {
      green: 'green',
      orange: 'warning',
      blue: 'link-info',
      red: 'error',
      white: 'primary',
      gray: 'gray',
    };
    return colorIconMap[color as keyof typeof colorIconMap] as TIconColor;
  };

  const handleNicknameClick = (clientUUID: string): void => {
    setSelectedClientUUID(clientUUID);
    setIsClientModalOpen(true);
  };

  const handleCloseModal = (): void => {
    setIsClientModalOpen(false);
    setSelectedClientUUID(null);
  };
  return (
    <>
      {isClientModalOpen && selectedClientUUID && !isDepositOpen && (
        <ClientModal
          clientUUID={selectedClientUUID}
          onClose={handleCloseModal}
          setIsDepositOpen={setIsDepositOpen}
        />
      )}
      {isDepositOpen && (
        <DepositModal
          defaultClientUUID={selectedClientUUID}
          onClose={() => {
            setIsDepositOpen(false);
            setIsClientModalOpen(false);
          }}
        />
      )}
      {events?.map((event) => {
        const config = LOG_CONFIGS_MAP[event.type];
        const Icon = config?.iconName ? getIcon(config.iconName) : null;
        const date = dayjs(event.timestamp);

        return (
          <div key={event.id} className={s.log}>
            <div className={s.top}>
              <div className={s.date}>
                <span className={s.day}>
                  {date.year() === dayjs().year()
                    ? dayjs(event.timestamp).format('DD MMM')
                    : `${dayjs(event.timestamp).format('DD MMM YY')} г.`}
                </span>
                <span className={s.time}>{date.format('HH:mm:ss')}</span>
              </div>
              <div className={s.type}>
                <span className={cn(s.category, s[`category_${config.color}`])}>
                  {config?.title}
                </span>
                {Icon && <Icon size="s" color={getIconColor(config.color)} />}
              </div>
            </div>
            {event.host && (
              <div className={s.host}>
                <ComputerIcon size="s" />
                <span>{event.host}</span>
              </div>
            )}
            <Description id={event.id} event={event} />
            <div className={s.footer}>
              {!event.subject ? (
                getGuest(t, event.type, false)
              ) : (
                <div
                  className={s.nickname}
                  onClick={() => {
                    if (event?.subject?.uuid)
                      handleNicknameClick(event?.subject?.uuid);
                  }}
                >
                  <WorkerIcon size="s" />
                  <span className={cn(s.user, 'subject')}>
                    {event.subject.nickname ?? event.subject.phone}
                  </span>
                </div>
              )}
              {event.object ? (
                <>
                  <svg
                    className={s.arrow}
                    width={event.subject ? '71' : '160'}
                    height="6"
                    viewBox={event.subject ? '0 0 71 6' : '0 0 160 6'}
                  >
                    <path
                      d={
                        event.subject
                          ? 'M71 3L66 0.113249V5.88675L71 3ZM0 3.5H66.5V2.5H0V3.5Z'
                          : 'M160 3L155 0.113249V5.88675L160 3ZM0 3.5H155.5V2.5H0V3.5Z'
                      }
                      fillOpacity="0.4"
                    />
                  </svg>
                  <div
                    className={s.nickname}
                    onClick={() => {
                      if (event?.object?.uuid)
                        handleNicknameClick(event?.object?.uuid);
                    }}
                  >
                    <WorkerIcon size="s" />
                    <span className={cn(s.user, 'object')}>
                      {event.object.nickname ?? event.object.phone}
                    </span>
                  </div>
                </>
              ) : (
                getGuest(t, event.type, true)
              )}
            </div>
          </div>
        );
      })}
    </>
  );
};
